"use client";

import { useEffect, useState, useRef, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { cn } from "@/app/lib/utils/cn";
import ThemeToggle from "@/app/ui/atomic/content/ThemeToggle";
import AuthNav from "@/app/ui/atomic/content/AuthNav";
import { services } from "@/app/lib/constants/services";
import { useTheme } from "@/app/lib/context/ThemeContext";

const serviceItems = services.map((s) => ({
  name: s.name,
  description: s.navDescription,
  link: s.link,
  goldText: s.goldText,
  pinkText: s.pinkText,
}));

const roleItems = [
  { name: "Co-Op Basics", goldText: "Co-Op\u00A0", pinkText: "Basics", link: "/get-started/what-are-worker-coops", description: "Learn how worker co-ops work" },
  { name: "Investors", goldText: "Inves", pinkText: "tors", link: "/get-started/investors", description: "Support democratic workplaces" },
  { name: "Entrepreneurs", goldText: "Entrepren", pinkText: "eurs", link: "/get-started/entrepreneurs", description: "Start your organization" },
  { name: "Business Owners", goldText: "Business\u00A0", pinkText: "Owners", link: "/get-started/business-owners", description: "Plan your exit strategy" },
  { name: "Supporters", goldText: "Suppor", pinkText: "ters", link: "/get-started/supporters", description: "Shop, work, or spread the word" },
  { name: "Experts", goldText: "Ex", pinkText: "perts", link: "/get-started/experts", description: "Review proposals and mentor organizations" },
];

const formItems = [
  { name: "Contact Us", goldText: "Contact\u00A0", pinkText: "Us", link: "/connect/contact", description: "Get in touch with our team" },
  { name: "Feedback", goldText: "Feed", pinkText: "back", link: "/connect/feedback", description: "Share your thoughts" },
];

const aboutItems = [
  { name: "About EconChangers", goldText: "About\u00A0", pinkText: "EconChangers", link: "/about-us", description: "Our story, goals, and values" },
  { name: "Our Team", goldText: "Our\u00A0", pinkText: "Team", link: "/about-us/team", description: "Meet the people behind the mission" },
  { name: "Why A Flamingo?", goldText: "Why A\u00A0", pinkText: "Flamingo?", link: "/about-us/flamingo", description: "The story behind our symbol" },
  { name: "FAQ", goldText: "F", pinkText: "AQ", link: "/about-us/faq", description: "Frequently asked questions" },
];

type DropdownType = "services" | "getStarted" | "about" | "connect" | null;

const Nav = () => {
  const pathname = usePathname();
  const { theme, mounted: themeMounted } = useTheme();
  const isDark = themeMounted && theme === "dark";
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<DropdownType>(null);
  const [mounted, setMounted] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setMounted(true); // eslint-disable-line react-hooks/set-state-in-effect -- hydration mount flag
  }, []);

  useEffect(() => {
    const html = document.getElementsByTagName("html")[0];
    if (html) html.dataset.mobileNavIsOpen = isOpen ? "true" : "false";
  }, [isOpen]);

  /* eslint-disable react-hooks/set-state-in-effect -- reset nav state on route change */
  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [pathname]);
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-nav-dropdown]")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && activeDropdown) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [activeDropdown]);

  const handleMouseEnter = useCallback((dropdown: DropdownType) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setActiveDropdown(dropdown);
  }, []);

  const handleMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  }, []);

  const toggleDropdown = (dropdown: DropdownType) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const isRoleActive = roleItems.some((r) => pathname.startsWith(r.link));
  const isFormActive = formItems.some((f) => pathname.startsWith(f.link));
  const isServiceActive = serviceItems.some((s) => pathname.startsWith(s.link));
  const isAboutActive = pathname.startsWith("/about-us");
  const isBlogActive = pathname.startsWith("/blog");

  const dropdownMenuClasses = "absolute top-full pt-2 z-50";
  const dropdownPanelClasses = "bg-gradient-purple rounded-xl shadow-2xl p-3 border border-white/10 backdrop-blur-sm";
  const menuItemClasses = "block py-3 px-4 rounded-lg hover:bg-white/10 hover:scale-[1.02] transition-all duration-150 focus-ring group";

  return (
    <nav className="flex-1 font-['Font-Light'] flex items-end">
      {/* Desktop Navigation - centered in flex-1 container */}
      <div className="flex-1 hidden lg:flex lg:justify-center lg:items-end">
        <ul className="flex items-end gap-3 xl:gap-6 2xl:gap-8">
        <li
          className={cn(
            "text-base lg:text-lg xl:text-xl transition-colors relative whitespace-nowrap",
            pathname === "/" ? "text-(--brand)" : "text-(--purple) hover:text-(--brand) nav-text-dark"
          )}
        >
          {pathname === "/" && (
            <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-(--brand) rounded-full" />
          )}
          <Link href="/" className="inline-flex items-center py-1">Home</Link>
        </li>

        <li
          className={cn(
            "text-base lg:text-lg xl:text-xl transition-colors relative whitespace-nowrap",
            isServiceActive ? "text-(--brand)" : "text-(--purple) hover:text-(--brand) nav-text-dark"
          )}
        >
          {isServiceActive && (
            <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-(--brand) rounded-full" />
          )}
          <div 
            className="relative" 
            data-nav-dropdown
            onMouseEnter={() => handleMouseEnter("services")}
            onMouseLeave={handleMouseLeave}
          >
            <button
              type="button"
              onClick={() => toggleDropdown("services")}
              onKeyDown={(e) => {
                if (e.key === "Escape") setActiveDropdown(null);
              }}
              className="cursor-pointer flex items-center gap-1.5 focus-ring rounded py-1"
              aria-expanded={activeDropdown === "services"}
              aria-haspopup="true"
            >
              Services
              <svg
                className={cn("w-3 h-3 transition-transform duration-200", activeDropdown === "services" && "rotate-180")}
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <div
              className={cn(
                dropdownMenuClasses,
                "left-1/2 -translate-x-1/2",
                activeDropdown === "services" ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2 pointer-events-none"
              )}
              role="menu"
            >
              <div className={cn(dropdownPanelClasses, "min-w-[500px]")}>
                <div className="grid grid-cols-2 gap-1">
                  {serviceItems.map((service) => (
                    <Link
                      key={service.name}
                      href={service.link}
                      className={menuItemClasses}
                      role="menuitem"
                    >
                      <span className="inline-block text-lg font-bold mb-1 group-hover:scale-105 transition-transform">
                        <span className="text-(--gold) font-['Font-Bold']">{service.goldText}</span>
                        <span className="text-(--pink) font-['Font-Bold']">{service.pinkText}</span>
                      </span>
                      <p className="text-white/70 text-xs mt-1 font-['Poppins'] whitespace-normal">{service.description}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </li>

        <li
          className={cn(
            "text-base lg:text-lg xl:text-xl transition-colors relative whitespace-nowrap",
            isRoleActive ? "text-(--brand)" : "text-(--purple) hover:text-(--brand) nav-text-dark"
          )}
        >
          {isRoleActive && (
            <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-(--brand) rounded-full" />
          )}
          <div 
            className="relative" 
            data-nav-dropdown
            onMouseEnter={() => handleMouseEnter("getStarted")}
            onMouseLeave={handleMouseLeave}
          >
            <button
              type="button"
              onClick={() => toggleDropdown("getStarted")}
              onKeyDown={(e) => {
                if (e.key === "Escape") setActiveDropdown(null);
              }}
              className="cursor-pointer flex items-center gap-1.5 focus-ring rounded py-1"
              aria-expanded={activeDropdown === "getStarted"}
              aria-haspopup="true"
            >
              Get Started
              <svg
                className={cn("w-3 h-3 transition-transform duration-200", activeDropdown === "getStarted" && "rotate-180")}
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <div
              className={cn(
                dropdownMenuClasses,
                "left-1/2 -translate-x-1/2",
                activeDropdown === "getStarted" ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2 pointer-events-none"
              )}
              role="menu"
            >
              <div className={cn(dropdownPanelClasses, "min-w-[500px]")}>
                <div className="grid grid-cols-2 gap-1">
                  {roleItems.map((item) => (
                    <Link
                      key={item.name}
                      href={item.link}
                      className={menuItemClasses}
                      role="menuitem"
                    >
                      <span className="inline-block text-lg font-bold mb-1 group-hover:scale-105 transition-transform">
                        <span className="text-(--gold) font-['Font-Bold']">{item.goldText}</span>
                        <span className="text-(--pink) font-['Font-Bold']">{item.pinkText}</span>
                      </span>
                      <p className="text-white/70 text-xs mt-1 font-['Poppins'] whitespace-normal">{item.description}</p>
                    </Link>
                  ))}
                </div>
                <div className="border-t border-white/10 mt-2 pt-2 px-2">
                  <Link
                    href="/get-started/what-are-worker-coops"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all text-sm font-['Poppins']"
                    role="menuitem"
                  >
                    <svg className="w-4 h-4 text-(--gold)" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Not sure where to start? Learn the basics
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </li>

        <li
          className={cn(
            "text-base lg:text-lg xl:text-xl transition-colors relative whitespace-nowrap",
            isAboutActive ? "text-(--brand)" : "text-(--purple) hover:text-(--brand) nav-text-dark"
          )}
        >
          {isAboutActive && (
            <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-(--brand) rounded-full" />
          )}
          <div 
            className="relative" 
            data-nav-dropdown
            onMouseEnter={() => handleMouseEnter("about")}
            onMouseLeave={handleMouseLeave}
          >
            <button
              type="button"
              onClick={() => toggleDropdown("about")}
              onKeyDown={(e) => {
                if (e.key === "Escape") setActiveDropdown(null);
              }}
              className="cursor-pointer flex items-center gap-1.5 focus-ring rounded py-1"
              aria-expanded={activeDropdown === "about"}
              aria-haspopup="true"
            >
              About Us
              <svg
                className={cn("w-3 h-3 transition-transform duration-200", activeDropdown === "about" && "rotate-180")}
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <div
              className={cn(
                dropdownMenuClasses,
                "left-1/2 -translate-x-1/2",
                activeDropdown === "about" ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2 pointer-events-none"
              )}
              role="menu"
            >
              <div className={cn(dropdownPanelClasses, "min-w-[280px]")}>
                <div className="grid grid-cols-1 gap-1">
                  {aboutItems.map((item) => (
                    <Link
                      key={item.name}
                      href={item.link}
                      className={menuItemClasses}
                      role="menuitem"
                    >
                      <span className="inline-block text-lg font-bold mb-1 group-hover:scale-105 transition-transform">
                        <span className="text-(--gold) font-['Font-Bold']">{item.goldText}</span>
                        <span className="text-(--pink) font-['Font-Bold']">{item.pinkText}</span>
                      </span>
                      <p className="text-white/70 text-xs mt-1 font-['Poppins'] whitespace-normal">{item.description}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </li>

        <li
          className={cn(
            "text-base lg:text-lg xl:text-xl transition-colors relative whitespace-nowrap",
            isBlogActive ? "text-(--brand)" : "text-(--purple) hover:text-(--brand) nav-text-dark"
          )}
        >
          {isBlogActive && (
            <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-(--brand) rounded-full" />
          )}
          <Link
            href="/blog"
            className="inline-flex items-center py-1 focus-ring rounded"
          >
            Blog
          </Link>
        </li>

        <li
          className={cn(
            "text-base lg:text-lg xl:text-xl transition-colors relative whitespace-nowrap",
            isFormActive ? "text-(--brand)" : "text-(--purple) hover:text-(--brand) nav-text-dark"
          )}
        >
          {isFormActive && (
            <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-(--brand) rounded-full" />
          )}
          <div 
            className="relative" 
            data-nav-dropdown
            onMouseEnter={() => handleMouseEnter("connect")}
            onMouseLeave={handleMouseLeave}
          >
            <button
              type="button"
              onClick={() => toggleDropdown("connect")}
              onKeyDown={(e) => {
                if (e.key === "Escape") setActiveDropdown(null);
              }}
              className="cursor-pointer flex items-center gap-1.5 focus-ring rounded py-1"
              aria-expanded={activeDropdown === "connect"}
              aria-haspopup="true"
            >
              Connect
              <svg
                className={cn("w-3 h-3 transition-transform duration-200", activeDropdown === "connect" && "rotate-180")}
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <div
              className={cn(
                dropdownMenuClasses,
                "right-0",
                activeDropdown === "connect" ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2 pointer-events-none"
              )}
              role="menu"
            >
              <div className={cn(dropdownPanelClasses, "min-w-[280px]")}>
                <div className="grid grid-cols-1 gap-1">
                  {formItems.map((item) => (
                    <Link
                      key={item.name}
                      href={item.link}
                      className={menuItemClasses}
                      role="menuitem"
                    >
                      <span className="inline-block text-lg font-bold mb-1 group-hover:scale-105 transition-transform">
                        <span className="text-(--gold) font-['Font-Bold']">{item.goldText}</span>
                        <span className="text-(--pink) font-['Font-Bold']">{item.pinkText}</span>
                      </span>
                      <p className="text-white/70 text-xs mt-1 font-['Poppins'] whitespace-normal">{item.description}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </li>
        </ul>
      </div>

      {/* Desktop Right Side Actions */}
      <div className="hidden lg:flex items-center lg:items-end gap-3 shrink-0">
        <ThemeToggle />
        <AuthNav variant="desktop" />
      </div>

      {/* Mobile layout (visible <lg) - Two row structure per style guide */}
      <div className="flex flex-col items-end gap-2 lg:hidden ml-auto">
        {/* Row 1: Theme toggle + Hamburger button */}
        <div className="flex items-center justify-around gap-3">
          {mounted && !isOpen && <ThemeToggle variant="header-mobile" />}
          
          {/* Animated Hamburger/X Button - single button for open/close */}
          {mounted && (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                "relative flex items-center justify-center w-11 h-11 min-w-11 min-h-11 rounded-xl",
                "transition-all duration-300 ease-in-out",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-(--gold)",
                isOpen 
                  ? "bg-(--brand) fixed top-4 right-4 z-[120]" 
                  : "bg-(--purple)"
              )}
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-expanded={isOpen}
            >
              <div className="relative flex flex-col justify-center items-center w-5 h-4">
                {/* Top line - rotates to form top of X */}
                <span 
                  className={cn(
                    "block w-full h-0.5 rounded-full absolute transition-all duration-300 ease-in-out",
                    isOpen 
                      ? "bg-(--purple) rotate-45 top-1/2 -translate-y-1/2" 
                      : "bg-(--gold) rotate-0 top-0"
                  )} 
                />
                {/* Middle line - fades out */}
                <span 
                  className={cn(
                    "block w-full h-0.5 rounded-full transition-all duration-300 ease-in-out",
                    isOpen 
                      ? "bg-(--purple) opacity-0 scale-0" 
                      : "bg-(--gold) opacity-100 scale-100"
                  )} 
                />
                {/* Bottom line - rotates to form bottom of X */}
                <span 
                  className={cn(
                    "block w-full h-0.5 rounded-full absolute transition-all duration-300 ease-in-out",
                    isOpen 
                      ? "bg-(--purple) -rotate-45 top-1/2 -translate-y-1/2" 
                      : "bg-(--gold) rotate-0 bottom-0"
                  )} 
                />
              </div>
            </button>
          )}
          
          {/* Placeholder for SSR - prevents layout shift */}
          {!mounted && (
            <div className="w-11 h-11 rounded-xl bg-(--purple)/50" aria-hidden="true" />
          )}
        </div>
        
        {/* Row 2: Auth button */}
        {mounted && !isOpen && <AuthNav variant="mobile" />}
      </div>

      {/* Mobile Nav Modal */}
      {isOpen && (
        <div className="fixed inset-0 w-full h-full bg-gradient-to-b from-(--purple) to-(--black) z-[100] overflow-y-auto scrollbar-hide lg:hidden mobile-menu-enter">
          {/* Sticky Header Bar */}
          <div className="sticky top-0 z-[110] flex justify-between items-center px-6 py-4 bg-(--purple)/95 backdrop-blur-sm border-b border-(--gold)/20">
            <span className="text-lg font-['Font-Bold'] text-(--gold)">Menu</span>
            {/* Spacer - matches the fixed hamburger button size */}
            <div className="w-11 h-11" aria-hidden="true" />
          </div>

          {/* Content Container */}
          <div className="flex flex-col min-h-full pt-6 pb-12 px-6">
            {/* Logo + Theme Toggle */}
            <div className="flex flex-col items-center gap-4 mb-8">
              <Link 
                href="/" 
                onClick={() => setIsOpen(false)} 
                className="relative w-[160px] h-[60px] rounded-lg overflow-hidden flex items-center justify-center"
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-0 bg-(--purple) rounded-lg shadow-lg border-2 border-(--gold)/30 transition-opacity duration-500"
                  style={{ opacity: isDark ? 0 : 1 }}
                />
                <Image
                  src="/logo-min-v2.png"
                  fill
                  sizes="160px"
                  alt="EconChangers"
                  className="object-contain p-2"
                />
              </Link>
              <div className="flex items-center gap-4">
                <ThemeToggle />
                <AuthNav variant="mobile" onNavigate={() => setIsOpen(false)} />
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1">
              {/* Home - Primary Link */}
              <ul className="space-y-2">
                <li>
                  <Link 
                    href="/" 
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      "block py-4 px-6 rounded-xl text-2xl font-['Font-Bold'] transition-all duration-200",
                      pathname === "/" 
                        ? "bg-(--gold) text-(--purple)" 
                        : "text-(--gold) hover:bg-(--gold)/20"
                    )}
                  >
                    Home
                  </Link>
                </li>
              </ul>

              {/* About Us Section */}
              <div className="mt-6 pt-6 border-t border-(--gold)/30">
                <h3 className="text-(--brand) text-lg font-['Font-Bold'] mb-4 px-6">About Us</h3>
                <ul className="grid grid-cols-2 gap-2">
                  {aboutItems.map((item) => {
                    const isExactMatch = pathname === item.link;
                    const isSubPageMatch = item.link !== "/about-us" && pathname.startsWith(item.link + "/");
                    const isActive = isExactMatch || isSubPageMatch;
                    return (
                      <li key={item.name}>
                        <Link 
                          href={item.link}
                          onClick={() => setIsOpen(false)}
                          className={cn(
                            "block py-3 px-4 text-center rounded-xl transition-all duration-200",
                            isActive
                              ? "bg-(--brand)/20 border border-(--brand)/40 text-(--brand)"
                              : "text-(--gold) hover:bg-(--gold)/10"
                          )}
                        >
                          <span className="text-(--gold) font-['Font-Bold']">{item.goldText}</span>
                          <span className="text-(--pink) font-['Font-Bold']">{item.pinkText}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Services Section */}
              <div className="mt-6 pt-6 border-t border-(--gold)/30">
                <h3 className="text-(--brand) text-lg font-['Font-Bold'] mb-4 px-6">Services</h3>
                <ul className="grid grid-cols-1 gap-2">
                  {serviceItems.map((service) => (
                    <li key={service.name}>
                      <Link 
                        href={service.link}
                        onClick={() => setIsOpen(false)}
                        className={cn(
                          "block py-3 px-6 rounded-xl transition-all duration-200",
                          pathname.startsWith(service.link)
                            ? "bg-(--brand)/20 border border-(--brand)/40"
                            : "hover:bg-(--gold)/10"
                        )}
                      >
                        <span className="text-(--gold) font-['Font-Bold'] text-xl">{service.goldText}</span>
                        <span className="text-(--pink) font-['Font-Bold'] text-xl">{service.pinkText}</span>
                        <p className="text-(--white)/70 text-sm mt-1">{service.description}</p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Get Started Section */}
              <div className="mt-6 pt-6 border-t border-(--gold)/30">
                <h3 className="text-(--brand) text-lg font-['Font-Bold'] mb-4 px-6">Get Started</h3>
                <ul className="grid grid-cols-2 gap-2">
                  {roleItems.map((item) => (
                    <li key={item.name}>
                      <Link 
                        href={item.link}
                        onClick={() => setIsOpen(false)}
                        className={cn(
                          "block py-3 px-4 text-center rounded-xl transition-all duration-200",
                          pathname.startsWith(item.link)
                            ? "bg-(--brand)/20 border border-(--brand)/40 text-(--brand)"
                            : "text-(--gold) hover:bg-(--gold)/10"
                        )}
                      >
                        <span className="text-(--gold) font-['Font-Bold']">{item.goldText}</span>
                        <span className="text-(--pink) font-['Font-Bold']">{item.pinkText}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  href="/get-started/what-are-worker-coops"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-center gap-2 mt-3 py-2 px-4 rounded-lg text-(--white)/60 hover:text-(--white) hover:bg-(--gold)/10 transition-all text-sm font-['Poppins']"
                >
                  <svg className="w-4 h-4 text-(--gold)" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Not sure? Learn the basics
                </Link>
              </div>

              {/* Blog Section */}
              <div className="mt-6 pt-6 border-t border-(--gold)/30">
                <Link
                  href="/blog"
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    "block py-4 px-6 rounded-xl text-2xl font-['Font-Bold'] transition-all duration-200",
                    isBlogActive
                      ? "bg-(--brand)/20 border border-(--brand)/40 text-(--brand)"
                      : "text-(--gold) hover:bg-(--gold)/20"
                  )}
                >
                  <span className="text-(--gold) font-['Font-Bold']">Bl</span>
                  <span className="text-(--pink) font-['Font-Bold']">og</span>
                </Link>
              </div>

              {/* Connect Section */}
              <div className="mt-6 pt-6 border-t border-(--gold)/30">
                <h3 className="text-(--brand) text-lg font-['Font-Bold'] mb-4 px-6">Connect</h3>
                <ul className="grid grid-cols-2 gap-2">
                  {formItems.map((item) => (
                    <li key={item.name}>
                      <Link 
                        href={item.link}
                        onClick={() => setIsOpen(false)}
                        className={cn(
                          "block py-3 px-4 text-center rounded-xl transition-all duration-200",
                          pathname.startsWith(item.link)
                            ? "bg-(--brand)/20 border border-(--brand)/40 text-(--brand)"
                            : "text-(--gold) hover:bg-(--gold)/10"
                        )}
                      >
                        <span className="text-(--gold) font-['Font-Bold']">{item.goldText}</span>
                        <span className="text-(--pink) font-['Font-Bold']">{item.pinkText}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </nav>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Nav;
